/**
 * Iter114 — Multi-State Business Registration capture (FREE sources).
 *
 * Mirrors the Sunbiz UX but for 5 states (NY, TX live via Socrata; CA, GA,
 * NV via bulk-import). Master/super_admin only. Zero per-search cost —
 * every record we ingest comes from official open-data portals.
 */
import ClaireHelpButton from "../components/ClaireHelpButton";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, RefreshCw, Upload, MapPin, Database, Download } from "lucide-react";

import LeadDetailDrawer from "../components/LeadDetailDrawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATE_LABELS = {
  NY: { name: "New York", method: "Socrata (Active Corporations)", auto: true },
  TX: { name: "Texas", method: "Socrata (New Sales Tax Permits)", auto: true },
  CA: { name: "California", method: "Bulk CSV (BizFile monthly)", auto: false },
  GA: { name: "Georgia", method: "Bulk CSV (GA SOS monthly)", auto: false },
  NV: { name: "Nevada", method: "Clark County PDF oficial (novos negócios/mês)", auto: true },
};

export default function MultiState() {
  const [tab, setTab] = useState("dashboard");
  const [stats, setStats] = useState(null);
  const [sources, setSources] = useState([]);
  const [leads, setLeads] = useState([]);
  const [filterState, setFilterState] = useState("ALL");
  const [syncing, setSyncing] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const [openLead, setOpenLead] = useState(null);

  const downloadCsv = (st) => {
    const url = `${API}/multi-state/leads.csv${st && st !== "ALL" ? `?state=${st}` : ""}`;
    window.open(url, "_blank");
  };

  // Multi-state lead → shared LeadDetailDrawer shape. We keep the raw
  // multi_state row under `.multi_state` so the manual-contact patch
  // endpoint (TODO) and the ExternalLookups buttons can find it.
  const msToLead = (l) => ({
    id: l.id,
    name: l.company_name,
    sector: `${l.state} · ${l.filing_type || ""}`.trim(),
    address: [l.principal_address, l.principal_city, l.state, l.principal_zip]
      .filter(Boolean).join(", "),
    phone: l.phone,
    email: l.email,
    website: null,
    crm_status: l.status?.toUpperCase() || "NEW",
    lead_score_bucket: "warm",
    multi_state: {
      state: l.state,
      document_number: l.document_number,
      file_date: l.file_date,
      officer_name: l.officer_name,
      registered_agent: l.registered_agent,
      fetched_via: l.fetched_via,
      principal_city: l.principal_city,
      principal_state: l.state,
      company_name: l.company_name,
    },
  });

  const loadStats = async () => {
    try {
      const { data } = await axios.get(`${API}/multi-state/stats`);
      setStats(data);
    } catch (e) { toast.error("Falha ao carregar estatísticas"); }
  };

  const loadSources = async () => {
    try {
      const { data } = await axios.get(`${API}/multi-state/sources`);
      setSources(data.sources || []);
    } catch (_e) {}
  };

  const loadLeads = async () => {
    try {
      const params = { limit: 100 };
      if (filterState !== "ALL") params.state = filterState;
      const { data } = await axios.get(`${API}/multi-state/leads`, { params });
      setLeads(data.leads || []);
    } catch (_e) {}
  };

  useEffect(() => { loadStats(); loadSources(); }, []);
  useEffect(() => { loadLeads(); }, [filterState, stats]); // eslint-disable-line

  const runSync = async (states) => {
    setSyncing(true); setLastReport(null);
    try {
      const { data } = await axios.post(`${API}/multi-state/sync`, {
        states, days_back: 7, limit_per_state: 500,
      });
      setLastReport(data);
      toast.success(`Sync concluído · ${data.total_inserted} novos leads`);
      loadStats(); loadLeads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no sync");
    } finally { setSyncing(false); }
  };

  return (
    <div className="al-shell" data-testid="multi-state-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        <header className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-3xl font-bold text-[var(--al-text)]">
                Multi-State Capture
              </h1>
              <span className="px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-400 text-[10px] font-bold uppercase tracking-wide">
                FREE
              </span>
            </div>
            <p className="text-[var(--al-text-muted)] text-sm mt-1">
              Captura diária de novas empresas em <strong>5 estados</strong>{" "}
              via fontes oficiais públicas. Custo por pesquisa:{" "}
              <span className="text-emerald-400 font-semibold">$0,00</span>.
            </p>
          </div>
          <div className="flex gap-2">
            <ClaireHelpButton testid="multi-state"
              question="Como funciona a captura Multi-State — sincronizar NY, TX, NV, CA, GA e importar CSV?" />
            <button
              data-testid="ms-sync-auto-btn"
              onClick={() => runSync(["NY", "TX", "NV"])}
              disabled={syncing}
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#3B82F6] hover:bg-[#2563EB] text-white text-[13px] font-semibold disabled:opacity-50"
            >
              {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Sync NY + TX + NV agora
            </button>
          </div>
        </header>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[var(--al-border)] mb-5">
          {["dashboard", "leads", "bulk-import", "sources"].map((id) => (
            <button
              key={id}
              data-testid={`ms-tab-${id}`}
              onClick={() => setTab(id)}
              className={`px-4 py-2 text-[13px] font-semibold transition border-b-2 ${
                tab === id
                  ? "text-[var(--al-text)] border-[#3B82F6]"
                  : "text-[var(--al-text-muted)] border-transparent hover:text-[var(--al-text)]"
              }`}
            >
              {id === "bulk-import" ? "Bulk Import (CA/GA)" : id.charAt(0).toUpperCase() + id.slice(1)}
            </button>
          ))}
        </div>

        {tab === "dashboard" && stats && (
          <DashboardTab
            stats={stats}
            lastReport={lastReport}
            onStateClick={(st) => { setFilterState(st); setTab("leads"); }}
            onDownload={downloadCsv}
          />
        )}

        {tab === "leads" && (
          <LeadsTab
            leads={leads}
            filterState={filterState}
            setFilterState={setFilterState}
            onDownload={downloadCsv}
            onRowClick={(l) => setOpenLead(msToLead(l))}
          />
        )}

        {tab === "bulk-import" && (
          <BulkImportTab onSuccess={() => { loadStats(); loadLeads(); }} />
        )}

        {tab === "sources" && (
          <SourcesTab sources={sources} reload={loadSources} />
        )}

        {openLead && (
          <LeadDetailDrawer
            lead={openLead}
            onClose={() => setOpenLead(null)}
            onChanged={() => { loadLeads(); loadStats(); }}
          />
        )}
      </div>
    </div>
  );
}

function DashboardTab({ stats, lastReport, onStateClick, onDownload }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        {stats.states.map((s) => (
          <button
            key={s.state}
            data-testid={`ms-card-${s.state}`}
            onClick={() => onStateClick(s.state)}
            className="text-left rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] hover:border-[#3B82F6]/60 hover:bg-[var(--al-row)] p-4 transition cursor-pointer"
          >
            <div className="flex items-center justify-between mb-2">
              <span className="text-[var(--al-text)] font-bold">
                {STATE_LABELS[s.state].name}
              </span>
              {STATE_LABELS[s.state].auto ? (
                <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 text-[9px] font-bold">
                  AUTO
                </span>
              ) : (
                <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-500 text-[9px] font-bold">
                  BULK CSV
                </span>
              )}
            </div>
            <div className="text-3xl font-bold text-[var(--al-text)]">{s.total}</div>
            <div className="text-[11px] text-[var(--al-text-muted)] mt-1">
              {s.today} hoje · {s.last_7d} últimos 7d
            </div>
            <div className="text-[10px] text-[var(--al-text-muted)] mt-2 italic">
              {STATE_LABELS[s.state].method}
            </div>
            {s.total > 0 && (
              <div className="mt-2 flex items-center gap-2">
                <span className="text-[10.5px] text-[#3B82F6] font-semibold">
                  Ver leads →
                </span>
                <span
                  role="button"
                  tabIndex={0}
                  data-testid={`ms-download-${s.state}`}
                  onClick={(e) => { e.stopPropagation(); onDownload(s.state); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.stopPropagation(); onDownload(s.state); } }}
                  title="Baixar CSV deste estado"
                  className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[var(--al-border)] hover:bg-[#3B82F6] hover:text-white text-[10px] font-semibold text-[var(--al-text-muted)] cursor-pointer"
                >
                  <Download className="w-2.5 h-2.5" /> CSV
                </span>
              </div>
            )}
          </button>
        ))}
      </div>
      <div className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4 flex items-center justify-between">
        <div>
          <div className="text-[var(--al-text-muted)] text-[11px] uppercase tracking-wide mb-1">
            Total capturado
          </div>
          <div className="text-4xl font-bold text-emerald-400">{stats.grand_total}</div>
        </div>
        <button
          data-testid="ms-download-all"
          onClick={() => onDownload("ALL")}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold"
        >
          <Download className="w-4 h-4" /> Baixar tudo (CSV)
        </button>
      </div>
      {lastReport && (
        <div className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4">
          <div className="text-[var(--al-text)] font-semibold mb-2">
            Último sync
          </div>
          <pre className="text-[11px] text-[var(--al-text-muted)] overflow-auto">
            {JSON.stringify(lastReport.reports, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function LeadsTab({ leads, filterState, setFilterState, onDownload, onRowClick }) {
  return (
    <div>
      <div className="flex gap-2 mb-4 items-center">
        {["ALL", "NY", "TX", "CA", "GA", "NV"].map((s) => (
          <button
            key={s}
            data-testid={`ms-filter-${s}`}
            onClick={() => setFilterState(s)}
            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold ${
              filterState === s
                ? "bg-[#3B82F6] text-white"
                : "bg-[var(--al-row-alt)] text-[var(--al-text-muted)]"
            }`}
          >
            {s}
          </button>
        ))}
        <button
          data-testid="ms-download-current"
          onClick={() => onDownload(filterState)}
          className="ml-auto inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-semibold"
        >
          <Download className="w-3.5 h-3.5" /> Baixar CSV
        </button>
      </div>
      <div className="rounded-xl border border-[var(--al-border)] overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead className="bg-[var(--al-row-alt)] text-[var(--al-text-muted)] text-[10.5px] uppercase tracking-wide">
            <tr>
              <th className="text-left px-3 py-2">State</th>
              <th className="text-left px-3 py-2">Doc #</th>
              <th className="text-left px-3 py-2">Empresa</th>
              <th className="text-left px-3 py-2">Cidade</th>
              <th className="text-left px-3 py-2">Filing date</th>
              <th className="text-left px-3 py-2">Source</th>
            </tr>
          </thead>
          <tbody>
            {leads.map((l) => (
              <tr
                key={`${l.state}-${l.document_number}`}
                onClick={() => onRowClick(l)}
                className="border-t border-[var(--al-border)] hover:bg-[var(--al-row)] cursor-pointer"
                data-testid={`ms-row-${l.id}`}
              >
                <td className="px-3 py-2 font-bold">{l.state}</td>
                <td className="px-3 py-2 text-[var(--al-text-muted)]">{l.document_number}</td>
                <td className="px-3 py-2 text-[var(--al-text)]">{l.company_name}</td>
                <td className="px-3 py-2 text-[var(--al-text-muted)]">{l.principal_city}</td>
                <td className="px-3 py-2 text-[var(--al-text-muted)]">{l.file_date}</td>
                <td className="px-3 py-2 text-[var(--al-text-muted)] text-[10.5px]">{l.fetched_via}</td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-[var(--al-text-muted)]">
                  Nenhum lead capturado ainda. Clique em "Sync NY + TX agora".
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BulkImportTab({ onSuccess }) {
  const [state, setState] = useState("CA");
  const [file, setFile] = useState(null);
  const [columnMap, setColumnMap] = useState(
    JSON.stringify({
      document_number: "ENTITY_ID",
      company_name: "CORP_NAME",
      principal_address: "ADDRESS",
      principal_city: "CITY",
      principal_zip: "ZIP",
      file_date: "DATE_FILED",
    }, null, 2)
  );
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const upload = async () => {
    if (!file) { toast.error("Selecione um arquivo"); return; }
    try {
      JSON.parse(columnMap);
    } catch (_e) { toast.error("column_map inválido (JSON malformado)"); return; }
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(
        `${API}/multi-state/bulk-import?state=${state}&column_map=${encodeURIComponent(columnMap)}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setResult(data);
      toast.success(`${data.inserted} leads importados`);
      onSuccess?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha no bulk import");
    } finally { setUploading(false); }
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4 text-[12.5px] text-[var(--al-text-muted)]">
        <div className="font-semibold text-[var(--al-text)] mb-1.5">
          Como usar o Bulk Import
        </div>
        <ol className="list-decimal ml-4 space-y-1">
          <li>Baixe o arquivo oficial CSV do estado (CA SOS BizFile, GA SOS, NV SilverFlume).</li>
          <li>Abra-o e identifique os nomes exatos das colunas no cabeçalho.</li>
          <li>Edite o <code>column_map</code> abaixo para mapear nossos campos canônicos → colunas do arquivo.</li>
          <li>Selecione o estado correto e faça upload. Linhas duplicadas (mesmo <code>document_number</code>) são puladas.</li>
        </ol>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] mb-1 block">
            Estado
          </label>
          <select
            data-testid="ms-bulk-state"
            value={state}
            onChange={(e) => setState(e.target.value)}
            className="al-input h-10 w-full"
          >
            {["CA", "GA", "NV", "NY", "TX"].map((s) => (
              <option key={s} value={s}>{s} — {STATE_LABELS[s].name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] mb-1 block">
            Arquivo CSV ou JSONL
          </label>
          <input
            type="file"
            data-testid="ms-bulk-file"
            accept=".csv,.jsonl,.ndjson"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="text-[12px] text-[var(--al-text-muted)]"
          />
        </div>
      </div>
      <div>
        <label className="text-[10.5px] uppercase tracking-wide text-[var(--al-text-muted)] mb-1 block">
          Column map (JSON)
        </label>
        <textarea
          data-testid="ms-bulk-colmap"
          value={columnMap}
          onChange={(e) => setColumnMap(e.target.value)}
          rows={8}
          className="al-input w-full font-mono text-[11.5px]"
        />
      </div>
      <button
        data-testid="ms-bulk-submit"
        onClick={upload}
        disabled={uploading || !file}
        className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[13px] font-semibold disabled:opacity-50"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        Importar
      </button>
      {result && (
        <div className="rounded-xl border border-[var(--al-border)] bg-[var(--al-row-alt)] p-4">
          <pre className="text-[11px] text-[var(--al-text-muted)]">
            {JSON.stringify(result, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

function SourcesTab({ sources, reload }) {
  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <p className="text-[var(--al-text-muted)] text-[12.5px]">
          Health-check das fontes oficiais públicas.
        </p>
        <button
          onClick={reload}
          className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[var(--al-row-alt)] hover:bg-[var(--al-border)] text-[12px]"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Re-check
        </button>
      </div>
      <div className="space-y-2">
        {sources.map((s) => (
          <div
            key={s.state}
            data-testid={`ms-source-${s.state}`}
            className="flex items-center gap-3 rounded-lg border border-[var(--al-border)] bg-[var(--al-row-alt)] p-3"
          >
            <span className={`w-2.5 h-2.5 rounded-full ${s.ok ? "bg-emerald-500" : "bg-amber-500"}`} />
            <span className="font-bold text-[var(--al-text)] w-12">{s.state}</span>
            <span className="text-[var(--al-text-muted)] text-[12px] flex-1">
              {STATE_LABELS[s.state]?.method || "—"}
            </span>
            <span className="text-[10.5px] text-[var(--al-text-muted)]">
              {s.ok ? "Online" : (s.status || s.error || "Offline")}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
